import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { api, apiErr } from "@/lib/api";
import { mediaUrl } from "@/pages/Marketplace";
import { toast } from "sonner";
import { MapPin, Eye, Bookmark, Share2, ExternalLink, Tag, User } from "lucide-react";

const CTA_ACTS = {
  "Visit Website": "website", "Learn More": "website", "Apply Now": "website", "Book Now": "website", "Get Directions": "website",
  "Call Now": "contact", "Contact Seller": "contact", "Send Message": "contact",
};

export default function AdDetail() {
  const { id } = useParams();
  const [ad, setAd] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [saved, setSaved] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get(`/ads/${id}`).then((r) => { setAd(r.data.ad); setSimilar(r.data.similar); setActive(0); }).catch(() => setAd(false));
  }, [id]);

  const track = (t) => api.post(`/ads/${id}/event`, { event_type: t }).catch(() => {});

  const cta = () => {
    track("click");
    const kind = CTA_ACTS[ad.cta] || "website";
    if (kind === "website" && ad.website) { track("website_visit"); window.open(ad.website, "_blank", "noopener"); }
    else if (ad.contact) { track("contact"); toast.success(`Contact: ${ad.contact}`); }
    else toast.info("No contact details provided.");
  };

  const toggleSave = async () => {
    try { const { data } = await api.post(`/ads/${id}/save`); setSaved(data.saved); toast.success(data.saved ? "Saved" : "Removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); track("share"); };

  if (ad === false) return (
    <div className="min-h-screen bg-[#0A0A14] text-slate-200"><Header /><div className="max-w-3xl mx-auto px-6 py-20"><BackButton className="mb-6" /><p className="text-slate-400">Ad not found.</p></div></div>
  );
  if (!ad) return <div className="min-h-screen bg-[#0A0A14] text-slate-200"><Header /><div className="max-w-3xl mx-auto px-6 py-20 text-slate-500">Loading…</div></div>;

  const imgs = ad.image_urls || [];

  return (
    <div className="min-h-screen bg-[#0A0A14] text-slate-200">
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="ad-detail">
        <BackButton className="mb-6" />
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <div className="aspect-[16/11] rounded-2xl bg-white/5 overflow-hidden border border-white/10">
              {imgs[active] ? <img src={mediaUrl(imgs[active])} alt={ad.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Tag className="w-10 h-10" /></div>}
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-2 mt-3">
                {imgs.map((u, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`w-16 h-16 rounded-lg overflow-hidden border ${i === active ? "border-[#A78BFA]" : "border-white/10"}`}>
                    <img src={mediaUrl(u)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs uppercase tracking-wider text-[#A78BFA]">{ad.category}</span>
              {ad.price && <span className="text-lg text-[#F5C842] ml-auto font-head font-700">{ad.price}</span>}
            </div>
            <h1 className="font-head font-900 text-3xl text-slate-50 mb-3" data-testid="ad-title">{ad.title}</h1>
            <div className="flex items-center gap-4 text-sm text-slate-500 mb-5">
              <span className="inline-flex items-center gap-1"><User className="w-4 h-4" /> {ad.advertiser_name}</span>
              {ad.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {ad.location}</span>}
              <span className="inline-flex items-center gap-1"><Eye className="w-4 h-4" /> {ad.views}</span>
            </div>
            <p className="text-slate-300 leading-relaxed whitespace-pre-line mb-6">{ad.description}</p>

            <div className="flex flex-wrap gap-3">
              <button data-testid="ad-cta-btn" onClick={cta} className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium bg-[#A78BFA] text-slate-900 hover:-translate-y-0.5 transition-transform duration-300">
                {ad.cta} <ExternalLink className="w-4 h-4" />
              </button>
              <button data-testid="ad-save-btn" onClick={toggleSave} className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm border transition-colors duration-300 ${saved ? "border-[#F5C842] text-[#F5C842]" : "border-white/15 text-slate-200 hover:border-white/40"}`}>
                <Bookmark className="w-4 h-4" /> {saved ? "Saved" : "Save"}
              </button>
              <button data-testid="ad-share-btn" onClick={share} className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm border border-white/15 text-slate-200 hover:border-white/40 transition-colors duration-300">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>
        </div>

        {similar.length > 0 && (
          <div className="mt-14">
            <h2 className="text-sm tracking-[0.25em] uppercase text-slate-400 mb-4">Similar ads</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="similar-ads">
              {similar.map((s) => (
                <Link key={s.id} to={`/market/${s.id}`} className="rounded-2xl border border-white/10 bg-[#11111A] overflow-hidden hover:-translate-y-1 transition-transform duration-200">
                  <div className="aspect-[16/10] bg-white/5 overflow-hidden">
                    {s.image_urls?.[0] ? <img src={mediaUrl(s.image_urls[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Tag className="w-6 h-6" /></div>}
                  </div>
                  <div className="p-3"><h3 className="font-head font-700 text-sm text-slate-50 line-clamp-1">{s.title}</h3></div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
